import React, { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { UserInfo } from "../models/user-info";
import { Toast } from "../services/toast-service";
import {
  addUserRole,
  deleteUserRole,
  getUserRoles,
  updateUserRole,
  User,
} from "../services/user-role.service";

type AccessScreenProps = {
  userInfo: UserInfo;
};

const AccessScreen: React.FC<AccessScreenProps> = (props) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "editor",
  });

  const [editingUser, setEditingUser] = useState<User>();
  const [message, setMessage] = useState({ text: "", type: "" });

  const showMessage = (text: string, type: string) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  const validateEmail = (email: string) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const handleRoleChange = (email: string, newRole: string) => {
    setUsers(
      users.map((user) =>
        user.email === email ? { ...user, role: newRole } : user,
      ),
    );
    setEditingUser({
      ...editingUser,
      role: newRole,
    } as User);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newUser.name.trim() || !newUser.email.trim()) {
      showMessage("Name and email are required", "error");
      return;
    }

    if (!validateEmail(newUser.email)) {
      showMessage("Please enter a valid email address", "error");
      return;
    }

    const emailExists = users.some((user) => user.email === newUser.email);
    if (emailExists) {
      showMessage("This email is already registered", "error");
      return;
    }

    try {
      await addUserRole(newUser);
      setUsers([
        ...users,
        {
          ...newUser,
        },
      ]);
      setNewUser({
        name: "",
        email: "",
        role: "editor",
      });
      Toast("Success!!", "Role assigned successfully!", "success");
    } catch (error) {
      Toast("Error!!", "Unable to assign the role", "danger");
    }
  };

  const handleDeleteUser = async (email: string) => {
    try {
      setLoading(true);
      await deleteUserRole(email);
      await getUsers();
      Toast("Success!!", "Role deleted successfully!", "success");
    } catch (error) {
      Toast("Error!!", "Unable to delete the user", "danger");
      return;
    }
    setLoading(false);
  };

  const startEditing = (user: User) => {
    setEditingUser({ ...user });
  };

  const handleEditUser: React.MouseEventHandler<HTMLButtonElement> = async (
    e,
  ) => {
    e.preventDefault();

    if (!editingUser) {
      return;
    }

    if (!editingUser.name.trim() || !editingUser.email.trim()) {
      showMessage("Name and email are required", "error");
      return;
    }

    if (!validateEmail(editingUser.email)) {
      showMessage("Please enter a valid email address", "error");
      return;
    }

    const emailExists = users.some(
      (user) =>
        user.email === editingUser.email && user.email !== editingUser.email,
    );
    if (emailExists) {
      showMessage("This email is already registered", "error");
      return;
    }
    setLoading(true);
    try {
      await updateUserRole(editingUser.email, editingUser.role);
      setUsers(
        users.map((user) =>
          user.email === editingUser.email ? editingUser : user,
        ),
      );
      setEditingUser(undefined);
      Toast("Success!!", "User role updated successfully!", "success");
    } catch (error) {
      Toast("Error!!", "Unable to update the user role", "danger");
    }
    setLoading(false);
  };

  const getUsers = async () => {
    const users = await getUserRoles();
    setUsers(
      users?.map((user: any) => ({
        name: user.Name,
        email: user.Email,
        role: user.Role,
      })) || [],
    );
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await getUsers();
      setLoading(false);
    })();
  }, []);

  return (
    <Layout userInfo={props.userInfo} loading={loading}>
      <div className="p-10">
        <h2 className="text-2xl font-bold mb-4">User Role Management</h2>

        {/* Message Alert */}
        {message.text && (
          <div
            className={`mb-4 p-4 rounded-md ${
              message.type === "error"
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Add User Form */}
        <form
          onSubmit={handleAddUser}
          className="mb-6 p-4 bg-gray-50 rounded-lg"
        >
          <div className="flex flex-wrap gap-4">
            <input
              type="text"
              placeholder="Name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              className="flex-1 min-w-[200px] px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="email"
              placeholder="Email"
              value={newUser.email}
              onChange={(e) =>
                setNewUser({ ...newUser, email: e.target.value })
              }
              className="flex-1 min-w-[200px] px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="approver">Approver</option>
              <option value="coordinator">Coordinator</option>
              <option value="editor">Editor</option>
            </select>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Add User
            </button>
          </div>
        </form>

        {/* Users Table */}
        <div className="border rounded-lg shadow-sm overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users?.map((user) => (
                <tr key={user.email}>
                  <td className="px-6 py-4 whitespace-nowrap">{user.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingUser?.email === user.email ? (
                      <select
                        value={user.role}
                        onChange={(e) =>
                          handleRoleChange(user.email, e.target.value)
                        }
                        className="py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        <option value="approver">Approver</option>
                        <option value="coordinator">Coordinator</option>
                        <option value="editor">Editor</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                    ${
                      user.role === "editor"
                        ? "bg-blue-100 text-blue-800"
                        : user.role === "coordinator"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-green-100 text-green-800"
                    }`}
                      >
                        {user.role}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      {editingUser?.email === user.email ? (
                        <>
                          <button
                            onClick={handleEditUser}
                            className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 cursor-pointer"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingUser(undefined)}
                            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEditing(user)}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.email)}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default AccessScreen;
